import { useState } from 'react';
import { PageHeader, useLayout } from '@hana/shell';
import { Button, Skeleton, StateView, Table, useToast, type Column } from '@hana/ui';
import { useT } from '@hana/i18n';
import './leave.i18n';
import { StateCard, meeStateProps } from '../meeState';
import { IconArrowBackUp, IconPlus } from '@tabler/icons-react';
import { leaveBalance, otHours, type MeeRequest } from '../data';
import { LeaveDonut, RouteSteps, StatusChip, TypeIcon } from '../Parts';
import { RequestDrawer } from '../requests/RequestDrawer';
import { useMee } from '../useMee';
import s from '../mee.module.css';
import { num, qtyLabel, rangeLabel } from './view';

function LeaveBody({ m }: { m: ReturnType<typeof useMee> }) {
  const t = useT();
  const toast = useToast();
  const phone = useLayout() === 'phone';
  const me = m.me!;
  const lv = leaveBalance(m.cal, me, m.mine);
  const top = Math.max(1, ...lv.byMonth);
  const rows = [...m.mine].sort((a, b) => b.from.localeCompare(a.from) || b.id.localeCompare(a.id));

  const pull = (id: string) => toast({ msg: `Đã rút đơn ${id}`, undo: m.pull(id) });
  const cols: Column<MeeRequest>[] = [
    { key: 'type', header: '', width: 64, cell: (r) => <TypeIcon type={r.type} /> },
    { key: 'id', header: 'Mã đơn', minor: true, cell: (r) => r.id },
    { key: 'time', header: 'Thời gian', cell: (r) => rangeLabel(r) },
    { key: 'qty', header: 'Số lượng', minor: true, cell: (r) => qtyLabel(r, otHours(r)) },
    { key: 'route', header: 'Duyệt theo ghế', minor: true, cell: (r) => <RouteSteps route={me.route} status={r.status} /> },
    { key: 'st', header: 'Trạng thái', cell: (r) => <StatusChip status={r.status} /> },
    {
      key: 'act',
      header: '',
      width: 124,
      align: 'right',
      cell: (r) =>
        r.status === 'Pending' ? (
          <Button size="sm" variant="ghost" icon={<IconArrowBackUp size={16} />} aria-label={`Rút đơn ${r.id}`} onClick={() => pull(r.id)}>
            {t('lv.withdraw')}
          </Button>
        ) : null,
    },
  ];

  if (phone) {
    return (
      <div className={s.page}>
        {rows.length === 0 && <div className={s.none}>{t('lv.none')}</div>}
        {rows.map((r) => (
          <div key={r.id} className={`${s.card} ${s.reqCard}`}>
            <TypeIcon type={r.type} />
            <span className={s.main}>
              <b>{rangeLabel(r)}</b>
              <span>{[r.id, r.type === 'fix' ? r.sub : qtyLabel(r, otHours(r))].join(' · ')}</span>
            </span>
            <StatusChip status={r.status} />
            {r.status === 'Pending' && (
              <Button size="sm" variant="ghost" icon={<IconArrowBackUp size={16} />} aria-label={`Rút đơn ${r.id}`} onClick={() => pull(r.id)} />
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={s.page}>
      <div className={s.grid3}>
        <section className={s.card} aria-label={t('lv.fund')}>
          <div className={s.head}><b>{t('lv.year')}</b></div>
          <div className={s.donutRow}>
            <LeaveDonut {...lv} />
            <div className={s.legend}>
              <span><i className={s.dot} style={{ background: 'var(--pri)' }} />{t('lv.used')} <b>{num(lv.used)}</b></span>
              <span><i className={s.dot} style={{ background: 'var(--d-amber)' }} />{t('lv.pending')} <b>{num(lv.pending)}</b></span>
              <span><i className={s.dot} style={{ background: 'var(--d-gray)' }} />{t('lv.left')} <b>{num(lv.leftAfter)}</b></span>
            </div>
          </div>
        </section>
        <section className={s.card} aria-label={t('lv.byMonth')} style={{ gridColumn: 'span 2' }}>
          <div className={s.head}><b>{t('lv.taken')}</b></div>
          <div className={s.months} role="img" aria-label={`${t('lv.byMonth')}: ${lv.byMonth.map((v, i) => `T${i + 1} ${num(v)}`).join(', ')}`}>
            {lv.byMonth.map((v, i) => (
              <div key={i} data-on={v > 0 ? 1 : undefined} title={`T${i + 1}: ${num(v)} ngày`} style={{ height: `${Math.max(4, (v / top) * 100)}%` }} />
            ))}
          </div>
          <div className={s.monthLb}><span>T1</span><span>T6</span><span>T12</span></div>
        </section>
      </div>
      <section className={`${s.card} ${s.tableCard}`} aria-label={t('lv.mine')}>
        <Table label={t('lv.mine')} columns={cols} rows={rows} rowKey={(r) => r.id} />
        {rows.length === 0 && <div className={s.none} style={{ padding: 16 }}>{t('lv.none')}</div>}
      </section>
    </div>
  );
}

export function LeaveScreen() {
  const t = useT();
  const m = useMee();
  const [create, setCreate] = useState(false);
  return (
    <>
      <PageHeader
        title={t('lv.title')}
        sub={m.me ? t('lv.sub', { n: num(leaveBalance(m.cal, m.me, m.mine).left) }) : undefined}
        actions={m.state === 'ready' ? <Button variant="primary" icon={<IconPlus size={18} />} onClick={() => setCreate(true)}>{t('lv.create')}</Button> : undefined}
      />
      <StateCard state={m.state}>
        <StateView
          state={m.state}
          {...meeStateProps('leave', m.state, m.retry, () => setCreate(true))}
          skeleton={<div className={s.skel}><Skeleton rows={4} /><Skeleton rows={6} /></div>}
        >
          <LeaveBody m={m} />
          <RequestDrawer open={create} onOpenChange={setCreate} m={m} />
        </StateView>
      </StateCard>
    </>
  );
}
